import React from "react";
import { ActionIcon, Anchor, Box, Button, Group, Text } from "@mantine/core";
import { IconArrowRight, IconTrash } from "@tabler/icons-react";
import CommissionInput from "../CommissionInput";
import styles from "./ServiceCommissionField.module.css";

/** How many service rates the summary names before "+N more". */
export const SUMMARY_PREVIEW_COUNT = 3;

export interface ServiceCommissionFieldProps {
  value: string;
  onChange: (value: string) => void;
  onError: (error: string | null) => void;
  error?: string;
  disabled?: boolean;
  /** Feature flag: offer the service-specific screen at all. */
  allowServiceSpecific: boolean;
  /** The record pays service-specific rates (shown instead of one rate). */
  serviceSpecific: boolean;
  /** The services that earn something, e.g. "Balayage 50%" (A–Z). */
  previewRates: string[];
  onOpenServiceRates: () => void;
  /** Drop the service-specific rates and go back to one rate. */
  onUseOneRate: () => void;
}

/** "Balayage 50% · Full Highlights 45% · Root Touch-up $25 · +6 more". */
export const summarizeRates = (rates: string[]): string => {
  if (rates.length === 0) return "Every service earns 0%";
  const shown = rates.slice(0, SUMMARY_PREVIEW_COUNT);
  const more = rates.length - shown.length;
  return [...shown, ...(more > 0 ? [`+${more} more`] : [])].join(" · ");
};

/**
 * The commission form's "Service commission" field: one rate for every
 * service with a link to the service-specific screen — or, once that screen
 * was used, a summary card to edit those rates or go back to one rate.
 */
const ServiceCommissionField: React.FC<ServiceCommissionFieldProps> = ({
  value,
  onChange,
  onError,
  error,
  disabled = false,
  allowServiceSpecific,
  serviceSpecific,
  previewRates,
  onOpenServiceRates,
  onUseOneRate,
}) => {
  if (allowServiceSpecific && serviceSpecific) {
    return (
      <Box>
        <Text className={styles.label}>Service commission</Text>
        <Group className={styles.summary} wrap="nowrap" gap="sm">
          <Box className={styles.summaryText}>
            <Text size="sm" fw={600}>
              Service-specific commissions
            </Text>
            <Text size="xs" c="dimmed">
              {summarizeRates(previewRates)}
            </Text>
          </Box>
          <Button
            variant="default"
            size="xs"
            onClick={onOpenServiceRates}
            disabled={disabled}
          >
            Edit
          </Button>
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label="Use one rate for all services"
            onClick={onUseOneRate}
            disabled={disabled}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      </Box>
    );
  }
  return (
    <Box>
      <CommissionInput
        label="Service commission"
        placeholder="e.g. 25% or $15"
        value={value}
        onChange={onChange}
        onError={onError}
        error={error}
        disabled={disabled}
      />
      {allowServiceSpecific && (
        <Anchor
          component="button"
          type="button"
          size="xs"
          mt={6}
          onClick={onOpenServiceRates}
          disabled={disabled}
        >
          <Group gap={4} wrap="nowrap">
            Or set service-specific commissions
            <IconArrowRight size={12} />
          </Group>
        </Anchor>
      )}
    </Box>
  );
};

export default ServiceCommissionField;
